/**
 * The reports pane's figures, at a glance, on the dashboard — AR aging by
 * bucket, occupancy, lease expirations by window, deposits held, and the
 * compliance clock count. One small fetch (`/api/reports/summary`) rather
 * than the six report endpoints' full row sets: a dashboard glance needs
 * totals, not the underlying rows, and the same view-backed queries any
 * report page runs already got small before landing here (CLAUDE.md, step 9).
 *
 * Plain `api()` + `useEffect`, same as `DepositsCollected` — this is one
 * singleton object, not a list, so `useList`'s pagination envelope has
 * nothing to offer it.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api.js';
import { Widget } from './Widget.js';
import { MiniBarChart } from './charts/MiniBarChart.js';
import { OccupancyBar } from './charts/OccupancyBar.js';

interface ReportsSummary {
  arAgingBuckets: {
    bucket0to30: string;
    bucket31to60: string;
    bucket61to90: string;
    bucket90plus: string;
  };
  occupiedUnits: number;
  vacantUnits: number;
  leaseExpirationWindows: {
    within30: number;
    within60: number;
    within90: number;
    within120: number;
  };
  depositsHeldTotal: string;
  complianceOverdue: number;
  complianceDueSoon: number;
}

function money(value: string): string {
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function ReportsGlanceWidget() {
  const [summary, setSummary] = useState<ReportsSummary | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    api<ReportsSummary>('/reports/summary')
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Widget
      icon="chart"
      tone="blue"
      title="Reports at a glance"
      action={<Link to="/staff/reports">Full reports →</Link>}
    >
      {isLoading ? <p className="muted">Loading…</p> : null}
      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load the reports summary.'}
        </p>
      ) : null}

      {!isLoading && !error && summary ? (
        <div className="stack">
          <div>
            <p className="muted">AR aging</p>
            <MiniBarChart
              colorVar="--down"
              severity="ascending"
              data={[
                {
                  label: '0–30d',
                  value: Number(summary.arAgingBuckets.bucket0to30),
                  displayValue: money(summary.arAgingBuckets.bucket0to30),
                },
                {
                  label: '31–60d',
                  value: Number(summary.arAgingBuckets.bucket31to60),
                  displayValue: money(summary.arAgingBuckets.bucket31to60),
                },
                {
                  label: '61–90d',
                  value: Number(summary.arAgingBuckets.bucket61to90),
                  displayValue: money(summary.arAgingBuckets.bucket61to90),
                },
                {
                  label: '90+d',
                  value: Number(summary.arAgingBuckets.bucket90plus),
                  displayValue: money(summary.arAgingBuckets.bucket90plus),
                },
              ]}
            />
          </div>

          <div>
            <p className="muted">Occupancy</p>
            <OccupancyBar occupied={summary.occupiedUnits} vacant={summary.vacantUnits} />
          </div>

          <div>
            <p className="muted">Lease expirations</p>
            <MiniBarChart
              colorVar="--info"
              severity="descending"
              data={[
                {
                  label: '≤30d',
                  value: summary.leaseExpirationWindows.within30,
                  displayValue: String(summary.leaseExpirationWindows.within30),
                },
                {
                  label: '31–60d',
                  value: summary.leaseExpirationWindows.within60,
                  displayValue: String(summary.leaseExpirationWindows.within60),
                },
                {
                  label: '61–90d',
                  value: summary.leaseExpirationWindows.within90,
                  displayValue: String(summary.leaseExpirationWindows.within90),
                },
                {
                  label: '91–120d',
                  value: summary.leaseExpirationWindows.within120,
                  displayValue: String(summary.leaseExpirationWindows.within120),
                },
              ]}
            />
          </div>

          <div className="grid">
            <div className="stat">
              <span className="stat__value">{money(summary.depositsHeldTotal)}</span>
              <span className="stat__label">Deposits held</span>
            </div>
            <div className="stat">
              <span className="stat__value">{summary.complianceOverdue}</span>
              <span className="stat__label">Notices overdue</span>
            </div>
            <div className="stat">
              <span className="stat__value">{summary.complianceDueSoon}</span>
              <span className="stat__label">Due within 5 days</span>
            </div>
          </div>
        </div>
      ) : null}
    </Widget>
  );
}
